import React from 'react';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { injectIntl, IntlShape } from 'react-intl';
import { FormField } from '../../../global/form/form-field/form-field';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import OptionsButton from '../../../global/options-button/options-button';
import { AccountFormVM } from '../account-form.viewmodel';
import { ONLY_SPACES_REG_EXP } from '../../../../constants/regex.constants';

import './account-form-presentation.scss';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { ADD_TEXT, APPLY_TEXT, CANCEL_TEXT, MORE_BUTTON_TEXT } from '../../../../constants/common-labels.constants';

interface IProps {
    vm: AccountFormVM;
    intl: IntlShape;
}

const AccountFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { intl, vm } = props;

    const formDataVM = vm.getFormDataVM();

    const accountOptions = [
        hasPermission(Permission.DELETE_ACCOUNT) && {
            label: intl.formatMessage({
                id: 'oe.accounts.deleteAccount',
                description: 'Button label',
                defaultMessage: 'Delete Account',
            }),
            action: vm.onDeleteAccount,
            danger: true,
            disabled: vm.eventsCount !== 0,
        }
    ].filter(Boolean);

    if (!formDataVM) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form meeting-form'>
                    {intl.formatMessage({
                        id: 'oe.accounts.accountDataIsLoading',
                        description: 'Placeholder about loading account data',
                        defaultMessage: 'Loading account data...',
                    })}
                </div>
            } />;
    }

    const removeOnlySpacesFromField = mobx.action((value: string, key: string) => {
        if (!(value.replace(ONLY_SPACES_REG_EXP, '').length)) {
            formDataVM[key] = '';
        }
    });

    return (
        <CommonPopupBodyFooterLayout
            body={
                <div>
                    <div className='oe-form'>
                        {formDataVM && <div className='oe-form__body'>
                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.organization.organizationName',
                                        description: 'Organization Name title',
                                        defaultMessage: 'Organization Name',
                                    })
                                }>
                                <input
                                    type='text'
                                    className='oe-form-field__control'
                                    value={formDataVM.getName()}
                                    onChange={mobx.action(({ target: { value } }) => formDataVM.setName(value))}
                                    onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'name'))}
                                />
                                {formDataVM.showValidationErrors() ? <div className='oe-form-field__error'>{formDataVM.getNameError(intl)}</div> : null}
                            </FormField>
                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.common.salesforceAccountId',
                                        description: 'salesforceAccountId Name title',
                                        defaultMessage: 'Salesforce Account ID',
                                    })
                                }>
                                <input
                                    type='text'
                                    className='oe-form-field__control'
                                    value={formDataVM.getSalesforceAccountID()}
                                    onChange={mobx.action(({ target: { value } }) => formDataVM.setSalesforceAccountID(value))}
                                    onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'salesforceAccountId'))}
                                />
                                {formDataVM.showValidationErrors()
                                    ? <div className='oe-form-field__error'>
                                        {formDataVM.getSalesforceAccountIDError(intl)}
                                    </div>
                                    : null
                                }
                            </FormField>

                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.common.passportSSOIdentityProvider',
                                        description: 'passportSSOIdentityProvider Name title',
                                        defaultMessage: 'Passport SSO Identity Providers',
                                    })
                                }>
                                <textarea
                                    rows={2}
                                    className='oe-form-field__control'
                                    value={formDataVM.getAllowedProviders()}
                                    onChange={mobx.action(({ target: { value } }) => formDataVM.setAllowedProviders(value))}
                                />
                                {formDataVM.showValidationErrors()
                                    ? <div className='oe-form-field__error'>
                                        {formDataVM.getAllowedProvidersError(intl)}
                                    </div>
                                    : null
                                }
                            </FormField>
                            {formDataVM.getServerErrorMessage() && <div className='oe-form-field__error'>{formDataVM.getServerErrorMessage()}</div>}
                        </div>
                        }
                    </div>
                </div>
            }
            footer={
                <>
                    {vm.isSaving() &&
                        <span className='oe-popup-layout__footer-status-area'>
                            {intl.formatMessage({
                                id: 'oe.accounts.accountIsBeingSaved',
                                description: 'Placeholder about saving account',
                                defaultMessage: 'Account is being saved, please wait...',
                            })}
                        </span>
                    }
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(vm.accountId ? APPLY_TEXT : ADD_TEXT)}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => {
                                return vm.handleSubmit(intl);
                            }}
                            disabled={vm.isSaving()}
                        />
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(CANCEL_TEXT)}
                        />
                        {vm.accountId && !!accountOptions.length && <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={accountOptions}
                            position='top'
                            align='start'
                        />}
                    </div>
                </>
            }
        />
    );

}));

export { AccountFormPresentation };
